import React, { useState } from 'react';
import axios from 'axios';
import NotificationModal from '../NotificationModal';
import DragDropFileUpload from '../DragDropFileUpload';
import styles from './MissionsTab.module.css';

// Simple Markdown renderer for basic formatting
const SimpleMarkdown = ({ children }) => {
  if (!children) return null;
  
  const text = children.toString();
  
  // Clean up the text by removing any raw HTML tags that shouldn't be there
  let cleanText = text
    .replace(/<br\s*\/?>/gi, '\n')  // Convert <br> to newlines
    .replace(/<\/?[^>]+(>|$)/g, '')  // Remove any other HTML tags
    .replace(/\n\s*\n/g, '\n\n')     // Clean up multiple newlines
    .trim();
  
  // Convert basic Markdown to HTML
  let html = cleanText
    // Bold: **text** -> <strong>text</strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic: *text* -> <em>text</em>
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Headers: # Header -> <h4>Header</h4>
    .replace(/^### (.*$)/gim, '<h5>$1</h5>')
    .replace(/^## (.*$)/gim, '<h4>$1</h4>')
    .replace(/^# (.*$)/gim, '<h3>$1</h3>')
    // Line breaks
    .replace(/\n/g, '<br />');
  
  // Handle lists properly
  const lines = html.split('<br />');
  const processedLines = [];
  let inList = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim().startsWith('- ')) {
      // Start of a list
      if (!inList) {
        processedLines.push('<ul>');
        inList = true;
      }
      processedLines.push('<li>' + line.trim().substring(2) + '</li>');
    } else {
      // End of a list
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(line);
    }
  }
  
  // Close any open list
  if (inList) {
    processedLines.push('</ul>');
  }
  
  html = processedLines.join('<br />');
  
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

const MissionsTab = ({ missions, tabId = "missions", tabLabel = "My Missions", onMissionViewed, onStartMission }) => {
  const [selectedMission, setSelectedMission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [workspaceMission, setWorkspaceMission] = useState(null);
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [missionNotes, setMissionNotes] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'assigned':
        return styles.statusAssigned;
      case 'in-progress':
        return styles.statusInProgress;
      case 'completed':
        return styles.statusCompleted;
      case 'overdue':
        return styles.statusOverdue;
      default:
        return styles.statusAssigned;
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'assigned':
        return 'Assigned';
      case 'in-progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'overdue':
        return 'Overdue';
      default:
        return 'Assigned';
    }
  };

  const showNotification = (title, message, type = 'info') => {
    setNotificationModal({
      show: true,
      title,
      message,
      type
    });
  };

  const hideNotification = () => {
    setNotificationModal({
      show: false,
      title: '',
      message: '',
      type: 'info'
    });
  };

  const handleViewDetails = async (mission) => {
    setSelectedMission(mission);
    setShowModal(true);
    // Mark mission as viewed when details are opened
    if (onMissionViewed) {
      await onMissionViewed(mission._id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMission(null);
  };

  const handleStartMission = (mission) => {
    setWorkspaceMission(mission);
    setShowWorkspace(true);
    
    if (mission.progress) {
      if (mission.progress.completedTasks) {
        setCompletedTasks(new Set(mission.progress.completedTasks));
      }
      if (mission.progress.notes) {
        setMissionNotes(mission.progress.notes);
      }
      if (mission.progress.uploadedFiles && mission.progress.uploadedFiles.length > 0) {
        setUploadedFiles(mission.progress.uploadedFiles);
      }
    } else {
      console.error('❌ No progress data found for mission');
    }
  
    if (onStartMission) {
      onStartMission(mission._id);
    }
  };

  const handleCloseWorkspace = () => {
    setShowWorkspace(false);
    setWorkspaceMission(null);
    setCompletedTasks(new Set());
    setMissionNotes('');
    setUploadedFiles([]);
  };

  const handleFileSelect = (files) => {
    if (files && files.length > 0) {
      // Keep previously uploaded files (those with url property) and add new files
      const existingFiles = uploadedFiles.filter(f => f.url && !(f instanceof File));
      setUploadedFiles([...existingFiles, ...files]);
    }
  };

  const handleFileRemove = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAllFilesRemove = () => {
    // Only remove new files (File objects), keep previously uploaded ones
    setUploadedFiles(prev => prev.filter(f => f.url && !(f instanceof File)));
  };

  const handleTaskToggle = (taskIndex) => {
    setCompletedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskIndex)) {
        newSet.delete(taskIndex);
      } else {
        newSet.add(taskIndex);
      }
      return newSet;
    });
  };

  const handleSaveProgress = async () => {
    try {
      
      const response = await axios.post(`/api/mission-assignments/${workspaceMission._id}/save-progress`, {
        completedTasks: Array.from(completedTasks),
        notes: missionNotes
      });
      
      if (response.data.success) {
        showNotification('Success', 'Progress saved successfully!', 'success');
      }
    } catch (error) {
      // console.error('❌ Error saving progress:', error);
      showNotification('Error', 'Failed to save progress. Please try again.', error);
    }
  };

  const handleSubmitMission = async () => {
    try {
      // Validate that at least one response method is provided
      const newFiles = uploadedFiles.filter(file => file instanceof File);
      const hasTextResponse = missionNotes.trim().length > 0;
      const hasNewFiles = newFiles.length > 0;
      const hasPreviousFiles = uploadedFiles.filter(f => f.url && !(f instanceof File)).length > 0;
      
      if (!hasTextResponse && !hasNewFiles && !hasPreviousFiles) {
        showNotification('Error', 'Please provide a text response, upload a document, or both.', 'error');
        return;
      }

      const formData = new FormData();
      formData.append('completedTasks', JSON.stringify(Array.from(completedTasks)));
      
      // Always append notes if provided (even if empty string, backend will handle it)
      if (hasTextResponse) {
        formData.append('notes', missionNotes);
      }
      
      // Append new files if any are selected
      if (hasNewFiles) {
        newFiles.forEach((file) => {
          formData.append('files', file);
        });
      }
      
      const response = await axios.post(`/api/mission-assignments/${workspaceMission._id}/submit`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        handleCloseWorkspace();
        showNotification('Success', `Mission submitted successfully!${response.data.uploadedFiles ? ` ${response.data.uploadedFiles} file(s) uploaded.` : ''}`, 'success');
        // Refresh the missions list to show updated status
        window.location.reload();
      }
    } catch (error) {
      console.error('❌ Error submitting mission:', error);
      showNotification('Error', 'Failed to submit mission. Please try again.', 'error');
    }
  };

  return (
    <div tabId={tabId} tabLabel={tabLabel} className={`missions-tab-content ${styles.missionsTabContent}`}>
      {missions.length === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyStateText}>No missions assigned yet. Your teacher will assign missions to you soon!</p>
        </div>
      ) : (
        <div className={styles.missionGrid}>
          {missions.map((mission) => (
            <div key={mission._id} className={styles.missionGridItem}>
              <div className={`card h-100 ${styles.missionCard}`}>
                <div className={`card-body d-flex flex-column ${styles.missionCardBody}`}>
                  <h5 className={`card-title ${styles.missionCardTitle}`}>{mission.missionId?.title || mission.title}</h5>
                  
                  <div className={styles.missionMeta}>
                    <span className={`${styles.statusBadge} ${getStatusBadgeClass(mission.status)}`}>
                      {getStatusDisplayName(mission.status)}
                    </span>
                    {mission.progress?.reassignedAt && (
                      <span className="badge bg-warning me-1" title={`Re-assigned: ${mission.progress.reassignmentReason || 'Work needs improvement'}`}>
                        <i className="fas fa-redo me-1"></i>
                        Re-assigned
                      </span>
                    )}
                    <span className="badge bg-primary me-1">{mission.difficulty || 'Medium'}</span>
                    {(mission.dueDate || mission.missionId?.dueDate) && (
                      <span className="badge bg-info me-1">
                        Due: {new Date(mission.dueDate || mission.missionId?.dueDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  
                  <div className="card-content flex-grow-1">
                    <div className={styles.missionDescription}>
                      <SimpleMarkdown>{mission.missionId?.description || mission.description}</SimpleMarkdown>
                    </div>
                  </div>
                  
                  <div className={styles.missionMeta}>
                    {typeof mission.progress === 'number' && mission.progress > 0 && (
                      <div className="mb-2">
                        <small className="text-muted">Progress: {mission.progress}%</small>
                        <div className="progress" style={{ height: '8px' }}>
                          <div 
                            className="progress-bar" 
                            style={{ width: `${mission.progress}%`, backgroundColor: 'var(--lightGreen)' }}
                          ></div>
                        </div>
                      </div>
                    )}
                    {mission.score > 0 && (
                      <div className="mb-2">
                        <small className="text-muted">Score: {mission.score} points</small>
                      </div>
                    )}
                    {mission.teacherName && (
                      <div className="mb-2">
                        <small className="text-muted">Assigned by: {mission.teacherName}</small>
                      </div>
                    )}
                  </div>
                  
                  <div className={`d-flex justify-content-between mt-auto ${styles.missionActions}`}>
                    <button 
                      className={`btn btn-primary btn-sm ${styles.missionButton}`}
                      onClick={() => mission.status === 'completed' ? handleViewDetails(mission) : handleStartMission(mission)}
                    >
                      {mission.status === 'completed' 
                        ? 'View Details' 
                        : mission.status === 'in-progress' 
                          ? 'Continue Mission' 
                          : 'Start Mission'
                      }
                    </button>
                    {mission.status !== 'completed' && (
                      <button 
                        className={`btn btn-outline-secondary btn-sm ${styles.missionButtonOutline}`}
                        onClick={() => handleViewDetails(mission)}
                      >
                        View Details
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mission Details Modal */}
      {showModal && selectedMission && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{selectedMission.missionId?.title || selectedMission.title}</h3>
              <button className={styles.modalCloseButton} onClick={handleCloseModal}>
                ×
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.modalSection}>
                <h4 className={styles.modalSectionTitle}>Mission Details</h4>
                <div className={styles.modalDescription}>
                  <SimpleMarkdown>{selectedMission.missionId?.description || selectedMission.description}</SimpleMarkdown>
                </div>
              </div>

              <div className={styles.modalSection}>
                <h4 className={styles.modalSectionTitle}>Mission Information</h4>
                <div className={styles.modalInfoGrid}>
                  <div className={styles.modalInfoItem}>
                    <strong>Status:</strong>
                    <span className={`${styles.statusBadge} ${getStatusBadgeClass(selectedMission.status)}`}>
                      {getStatusDisplayName(selectedMission.status)}
                    </span>
                  </div>
                  
                  <div className={styles.modalInfoItem}>
                    <strong>Difficulty:</strong>
                    <span>{selectedMission.difficulty || 'Medium'}</span>
                  </div>
                  
                  {selectedMission.dueDate && (
                    <div className={styles.modalInfoItem}>
                      <strong>Due Date:</strong>
                      <span>{new Date(selectedMission.dueDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  {selectedMission.teacherName && (
                    <div className={styles.modalInfoItem}>
                      <strong>Assigned by:</strong>
                      <span>{selectedMission.teacherName}</span>
                    </div>
                  )}
                  
                  {selectedMission.progress > 0 && (
                    <div className={styles.modalInfoItem}>
                      <strong>Progress:</strong>
                      <span>{selectedMission.progress}%</span>
                    </div>
                  )}
                  
                  {selectedMission.score > 0 && (
                    <div className={styles.modalInfoItem}>
                      <strong>Score:</strong>
                      <span>{selectedMission.score} points</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                className={`btn btn-secondary ${styles.modalButton}`}
                onClick={handleCloseModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mission Workspace Modal */}
      {showWorkspace && workspaceMission && (
        <div className={styles.workspaceOverlay} onClick={handleCloseWorkspace}>
          <div className={styles.workspaceContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.workspaceHeader}>
              <h2 className={styles.workspaceTitle}>{workspaceMission.missionId?.title || workspaceMission.title}</h2>
              <button className={styles.workspaceCloseButton} onClick={handleCloseWorkspace}>
                ×
              </button>
            </div>
            
            <div className={styles.workspaceBody}>
              <div className={styles.workspaceSection}>
                <h3 className={styles.workspaceSectionTitle}>Mission Description</h3>
                <div className={styles.workspaceDescription}>
                  <SimpleMarkdown>{workspaceMission.missionId?.description || workspaceMission.description}</SimpleMarkdown>
                </div>
                
                {/* Re-assignment Notice */}
                {workspaceMission.progress?.reassignedAt && (
                  <div className="alert alert-warning mt-3">
                    <i className="fas fa-redo me-2"></i>
                    <strong>This mission was re-assigned by your teacher.</strong>
                    <br />
                    <small>
                      <strong>Reason:</strong> {workspaceMission.progress.reassignmentReason || 'Work needs improvement'}
                      <br />
                      <strong>Re-assigned on:</strong> {new Date(workspaceMission.progress.reassignedAt).toLocaleString()}
                    </small>
                    <br />
                    <small className="text-muted">
                      Your previous work has been preserved. You can continue from where you left off and make improvements.
                    </small>
                  </div>
                )}
                
                {/* Rich Content Section */}
                {workspaceMission.resources && (
                  <div className={styles.richContentSection}>
                    <h4 className={styles.richContentTitle}>Resources & Materials</h4>
                    <div className={styles.richContentGrid}>
                      {/* Video Resources */}
                      {workspaceMission.resources.includes('video') && (
                        <div className={styles.contentCard}>
                          <h5 className={styles.contentCardTitle}>📹 Video Resources</h5>
                          <div className={styles.videoPlaceholder}>
                            <p>Video content will be displayed here</p>
                            <small>Teachers can embed videos in mission descriptions</small>
                          </div>
                        </div>
                      )}
                      
                      {/* Document Resources */}
                      {workspaceMission.resources.includes('document') && (
                        <div className={styles.contentCard}>
                          <h5 className={styles.contentCardTitle}>📄 Documents</h5>
                          <div className={styles.documentPlaceholder}>
                            <p>PDF and document links will appear here</p>
                            <small>Teachers can attach reading materials</small>
                          </div>
                        </div>
                      )}
                      
                      {/* Interactive Resources */}
                      {workspaceMission.resources.includes('interactive') && (
                        <div className={styles.contentCard}>
                          <h5 className={styles.contentCardTitle}>🎮 Interactive Activities</h5>
                          <div className={styles.interactivePlaceholder}>
                            <p>Quizzes, puzzles, and interactive content</p>
                            <small>Teachers can add interactive elements</small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.workspaceSection}>
                <h3 className={styles.workspaceSectionTitle}>Your Tasks</h3>
                <div className={styles.taskList}>
                  {/* For now, create some default tasks based on mission content */}
                  {[
                    'Read through the mission description carefully',
                    'Complete the required activities',
                    'Take notes on important points',
                    'Review your work before submitting'
                  ].map((task, index) => (
                    <div key={index} className={styles.taskItem}>
                      <input
                        type="checkbox"
                        id={`task-${index}`}
                        checked={completedTasks.has(index)}
                        onChange={() => handleTaskToggle(index)}
                        className={styles.taskCheckbox}
                      />
                      <label htmlFor={`task-${index}`} className={styles.taskLabel}>
                        {task}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.workspaceSection}>
                <h3 className={styles.workspaceSectionTitle}>Your Response & Notes</h3>
                
                {/* Text Response - Always Available */}
                <div className="mb-4">
                  <label className="form-label">
                    <strong>Mission Response (Optional):</strong>
                    <small className="text-muted d-block">Write your detailed response to the mission here. Include your thoughts, answers, and any work you've completed.</small>
                  </label>
                  <textarea
                    className={`form-control ${styles.notesTextarea}`}
                    placeholder="Write your detailed response to the mission here. Include your thoughts, answers, and any work you've completed..."
                    value={missionNotes}
                    onChange={(e) => setMissionNotes(e.target.value)}
                    rows={8}
                    style={{ minHeight: '200px' }}
                  />
                </div>

                {/* File Upload - Always Available */}
                <div className="mb-4">
                  <label className="form-label">
                    <strong>Upload Supporting Documents (Optional):</strong>
                    <small className="text-muted d-block">Upload your response as a Word document, PDF, or other file format. You can upload multiple files. You can use this along with your typed response above, or on its own.</small>
                  </label>
                  <DragDropFileUpload
                    onFileSelected={handleFileSelect}
                    onFileRemoved={handleAllFilesRemove}
                    selectedFile={uploadedFiles.filter(f => f instanceof File || !f.url).length > 0 ? uploadedFiles.filter(f => f instanceof File || !f.url) : null}
                    accept=".doc,.docx,.pdf,.txt,.rtf"
                    multiple={true}
                    maxFileSize={10 * 1024 * 1024} // 10MB
                    supportedTypes={['.doc', '.docx', '.pdf', '.txt', '.rtf']}
                    placeholder="Click to upload files or drag and drop"
                    description="Supports: Word (.doc, .docx), PDF, Text (.txt), RTF"
                    className="mb-3"
                  />
                  
                  {/* Individual file removal for new files */}
                  {uploadedFiles.filter(f => f instanceof File || !f.url).length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">Click on individual files below to remove them:</small>
                      <ul className="list-group mt-2">
                        {uploadedFiles
                          .filter(f => f instanceof File || !f.url)
                          .map((file, _index) => {
                            const actualIndex = uploadedFiles.findIndex(f => 
                              (f instanceof File && file instanceof File && f === file) || 
                              (!f.url && !file.url && f === file)
                            );
                            return (
                              <li key={actualIndex} className="list-group-item d-flex justify-content-between align-items-center">
                                <span>
                                  <i className="fas fa-file me-2"></i>
                                  {file.name || file.originalname || `File ${actualIndex + 1}`}
                                  {file.size && <small className="text-muted ms-2">({(file.size / 1024).toFixed(2)} KB)</small>}
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleFileRemove(actualIndex)}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Previously uploaded files display */}
                {uploadedFiles.filter(f => f.url && !(f instanceof File)).length > 0 && (
                  <div className="mb-3">
                    <strong>Previously Uploaded Files:</strong>
                    <ul className="list-group mt-2">
                      {uploadedFiles
                        .filter(f => f.url && !(f instanceof File))
                        .map((file, index) => (
                          <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                            <a href={file.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                              <i className="fas fa-file me-2"></i>
                              {file.name}
                            </a>
                            <small className="text-muted">
                              Uploaded {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : ''}
                            </small>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

                <div className="text-muted small">
                  <i className="fas fa-info-circle me-1"></i>
                  You can provide a typed response, upload documents, or both. Your response will be visible to your teacher when you submit the mission.
                </div>
              </div>
            </div>
            
            <div className={styles.workspaceFooter}>
              <button 
                className={`btn btn-secondary ${styles.workspaceButton}`}
                onClick={handleCloseWorkspace}
              >
                Close
              </button>
              <button 
                className={`btn btn-info ${styles.workspaceButton} ${styles.workspaceButtonSave}`}
                onClick={handleSaveProgress}
              >
                Save Progress
              </button>
              <button 
                className={`btn btn-success ${styles.workspaceButton} ${styles.workspaceButtonSubmit}`}
                onClick={handleSubmitMission}
              >
                Submit Mission
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        show={notificationModal.show}
        onClose={hideNotification}
        title={notificationModal.title}
        message={notificationModal.message}
        type={notificationModal.type}
      />
    </div>
  );
};

export default MissionsTab; 